import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ShopItemStatus } from '../model/LanzacoinShopItem';
import { useAuthentication } from '../reducer/authentication';
import { LanzacoinShopApi } from '../service/LanzacoinShopApi';
import CommentTaskApi, { EmoteLimitInfo } from '../service/CommentTaskApi';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutos

interface LanzacoinShopContextData {
    shopItems: ShopItemStatus[];
    hasProfileHighlight: boolean;
    /** IDs de todos os usuários com destaque no perfil ativo */
    highlightedUserIds: Set<number>;
    /** Quantidade de dislikes disponíveis (comprados e não usados) */
    availableDislikes: number;
    /** Limites e uso diário de cada emote (inclui extras da loja) */
    emoteLimits: EmoteLimitInfo[];
    loading: boolean;
    refresh: () => Promise<void>;
}

const emptySet = new Set<number>();

const LanzacoinShopContext = createContext<LanzacoinShopContextData>({
    shopItems: [],
    hasProfileHighlight: false,
    highlightedUserIds: emptySet,
    availableDislikes: 0,
    emoteLimits: [],
    loading: false,
    refresh: async () => {},
});

const shopApi = new LanzacoinShopApi();
const commentTaskApi = new CommentTaskApi();

export function LanzacoinShopProvider({ children }: { children: React.ReactNode }) {
    const { user } = useAuthentication();
    const [shopItems, setShopItems] = useState<ShopItemStatus[]>([]);
    const [hasProfileHighlight, setHasProfileHighlight] = useState(false);
    const [highlightedUserIds, setHighlightedUserIds] = useState<Set<number>>(emptySet);
    const [availableDislikes, setAvailableDislikes] = useState(0);
    const [emoteLimits, setEmoteLimits] = useState<EmoteLimitInfo[]>([]);
    const [loading, setLoading] = useState(false);
    const intervalRef = useRef<NodeJS.Timer | null>(null);
    const activeRef = useRef(true);

    const fetchStatus = useCallback(async () => {
        if (!user?.use_lanza_coin) {
            setShopItems([]);
            setHasProfileHighlight(false);
            setHighlightedUserIds(emptySet);
            setAvailableDislikes(0);
            setEmoteLimits([]);
            return;
        }
        try {
            setLoading(true);
            const [items, activeIds, dislikeResult, limitsResult] = await Promise.all([
                shopApi.getShopStatus(),
                shopApi.getActiveHighlights(),
                commentTaskApi.getAvailableDislikes(),
                commentTaskApi.getEmoteLimits(),
            ]);
            if (!activeRef.current) return;
            setShopItems(items ?? []);
            const highlight = items?.find(i => i.slug === 'profile_highlight');
            setHasProfileHighlight(!!highlight?.active_bonus);
            setHighlightedUserIds(new Set(activeIds ?? []));
            setAvailableDislikes(dislikeResult?.available ?? 0);
            setEmoteLimits(limitsResult ?? []);
        } catch {
            if (!activeRef.current) return;
            setShopItems([]);
            setHasProfileHighlight(false);
            setHighlightedUserIds(emptySet);
            setAvailableDislikes(0);
            setEmoteLimits([]);
        } finally {
            if (activeRef.current) setLoading(false);
        }
    }, [user?.use_lanza_coin]);

    // Busca inicial + refresh periódico
    useEffect(() => {
        activeRef.current = true;

        if (!user?.use_lanza_coin) {
            setShopItems([]);
            setHasProfileHighlight(false);
            setHighlightedUserIds(emptySet);
            setAvailableDislikes(0);
            setEmoteLimits([]);
            return;
        }

        fetchStatus();

        intervalRef.current = setInterval(() => {
            fetchStatus();
        }, REFRESH_INTERVAL_MS);

        return () => {
            activeRef.current = false;
            if (intervalRef.current) {
                clearInterval(intervalRef.current as unknown as number);
                intervalRef.current = null;
            }
        };
    }, [user?.id, user?.use_lanza_coin, fetchStatus]);

    const refresh = useCallback(async () => {
        await fetchStatus();
    }, [fetchStatus]);

    return (
        <LanzacoinShopContext.Provider value={{ shopItems, hasProfileHighlight, highlightedUserIds, availableDislikes, emoteLimits, loading, refresh }}>
            {children}
        </LanzacoinShopContext.Provider>
    );
}

export function useLanzacoinShop(): LanzacoinShopContextData {
    return useContext(LanzacoinShopContext);
}

export default LanzacoinShopContext;
